/**
 * Unified Tabs Store
 *
 * Single source of truth for split view state management.
 * Each tab contains its own SplitNode tree and focused panel.
 * Persisted to localStorage.
 *
 * This replaces both the old TabsStore and SplitViewStore with a unified approach:
 * - No more bidirectional sync between stores
 * - Current tab's root/focusedPanelId are the "active" split view state
 * - All mutations happen through this single store
 */

import React from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useShallow } from "zustand/shallow";
import {
  SplitNode,
  SplitPanel,
  SplitOrientation,
  SplitGroupNode,
  SplitPanelType,
  getAllPanels,
  countPanels,
  findPanelInTree,
  generateId,
} from "@/hooks/use-split-view-store/types";
import logger from "@/utils/logger";

// ============================================================================
// Types
// ============================================================================

/**
 * A single tab containing a split view configuration.
 */
export interface Tab {
  /** Unique identifier for this tab */
  id: string;
  /** Display name for the tab */
  name: string;
  /** Root node of the split tree for this tab */
  root: SplitNode | null;
  /** ID of the focused panel on this tab */
  focusedPanelId: string | null;
  /** Creation timestamp */
  createdAt: number;
  /** Optional color for the tab (accent color) */
  color?: string;
  /** Optional icon for the tab */
  icon?: string;
}

/**
 * Store state.
 */
export interface TabsState {
  /** Array of all tabs */
  tabs: Tab[];
  /** ID of the currently active tab */
  currentTabId: string | null;
}

/**
 * Store actions - Tab management.
 */
export interface TabsActions {
  // --- Tab Management ---
  /** Get the current tab */
  getCurrentTab: () => Tab | null;
  /** Create a new tab */
  createTab: (name?: string, initialRoot?: SplitNode | null) => string;
  /** Delete a tab by ID */
  deleteTab: (tabId: string) => void;
  /** Select a tab by ID */
  selectTab: (tabId: string) => void;
  /** Rename a tab */
  renameTab: (tabId: string, name: string) => void;
  /** Get all tabs */
  getTabs: () => Tab[];
  /** Initialize with default tab if none exist */
  ensureDefaultTab: () => string;
  /** Reset to initial state */
  reset: () => void;
  /** Navigate to previous tab */
  goToPreviousTab: () => void;
  /** Navigate to next tab */
  goToNextTab: () => void;
  /** Navigate to tab by index (for keyboard shortcuts) */
  goToTabByIndex: (index: number) => void;
  /** Get the route to open this tab in a new browser tab */
  getTabRoute: (tabId: string) => string | null;
  /** Update tab color */
  updateTabColor: (tabId: string, color: string) => void;
  /** Update tab icon */
  updateTabIcon: (tabId: string, icon: string) => void;
  /** Remove empty tabs (auto-cleanup) */
  cleanupEmptyTabs: () => void;

  // --- Split View Actions (operate on current tab) ---
  /** Check if split view is active (current tab has > 1 panel) */
  isActive: () => boolean;
  /** Get all panels from current tab as flat array */
  getPanels: () => SplitPanel[];
  /** Add a panel to the current tab */
  addPanel: (
    panel: Omit<SplitPanel, "id">,
    targetPanelId?: string,
    orientation?: SplitOrientation,
    addBefore?: boolean,
  ) => string;
  /** Remove a panel from the current tab */
  removePanel: (panelId: string) => void;
  /** Set the focused panel in current tab */
  setFocusedPanelId: (panelId: string | null) => void;
  /** Update sizes for a group in current tab */
  updateGroupSizes: (groupId: string, sizes: number[]) => void;
  /** Update a panel's properties in current tab */
  updatePanel: (
    panelId: string,
    updates: Partial<Omit<SplitPanel, "id">>,
  ) => void;
  /** Swap two panels in current tab */
  swapPanels: (panelIdA: string, panelIdB: string) => void;
  /** Move a panel to a position relative to another panel */
  movePanel: (
    sourcePanelId: string,
    targetPanelId: string,
    position: "left" | "right" | "top" | "bottom",
  ) => void;
  /** Initialize current tab with a single panel */
  initializeWithPanel: (panel: Omit<SplitPanel, "id">) => string;
  /** Set the root node directly for current tab */
  setRoot: (root: SplitNode | null) => void;
  /** Move a panel to another tab */
  movePanelToTab: (panel: SplitPanel, targetTabId: string) => void;
  /**
   * Sync the current tab's panel with the browser route.
   *
   * Called by SplitViewProvider when the browser URL changes from external
   * navigation (back/forward, URL bar entry). NOT called for app-initiated
   * navigation which is handled by usePanelNavigation.navigateTo().
   *
   * The navigation guard prevents this from running after app navigation
   * to avoid double sync loops.
   *
   * @see navigation-guard.ts
   * @see SplitViewProvider sync effect
   */
  syncWithRoute: (pathname: string, search: string) => void;
}

export type TabsStore = TabsState & TabsActions;

// ============================================================================
// Tree Manipulation Helpers
// ============================================================================

/**
 * Find and update a group by ID in the tree.
 */
function updateGroupInTree(
  node: SplitNode,
  groupId: string,
  updater: (group: SplitGroupNode) => SplitGroupNode,
): SplitNode {
  if (node.type === "panel") {
    return node;
  }

  if (node.id === groupId) {
    return updater(node);
  }

  return {
    ...node,
    children: node.children.map((child) =>
      updateGroupInTree(child, groupId, updater),
    ),
  };
}

/**
 * Remove a panel from the tree and simplify structure.
 */
function removePanelFromTree(
  node: SplitNode,
  panelId: string,
): SplitNode | null {
  if (node.type === "panel") {
    return node.panel.id === panelId ? null : node;
  }

  const newChildren: SplitNode[] = [];
  const newSizes: number[] = [];
  let removedIndex = -1;

  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];
    const result = removePanelFromTree(child, panelId);

    if (result !== null) {
      newChildren.push(result);
      newSizes.push(node.sizes[i]);
    } else {
      removedIndex = i;
    }
  }

  if (newChildren.length === 0) {
    return null;
  }

  if (newChildren.length === 1) {
    return newChildren[0];
  }

  if (removedIndex !== -1) {
    const totalSize = newSizes.reduce((sum, s) => sum + s, 0);
    const normalizedSizes = newSizes.map((s) => (s / totalSize) * 100);
    return {
      ...node,
      children: newChildren,
      sizes: normalizedSizes,
    };
  }

  return {
    ...node,
    children: newChildren,
    sizes: newSizes,
  };
}

/**
 * Update a panel in the tree.
 */
function updatePanelInTree(
  node: SplitNode,
  panelId: string,
  updates: Partial<Omit<SplitPanel, "id">>,
): SplitNode {
  if (node.type === "panel") {
    if (node.panel.id === panelId) {
      return {
        ...node,
        panel: { ...node.panel, ...updates },
      };
    }
    return node;
  }

  return {
    ...node,
    children: node.children.map((child) =>
      updatePanelInTree(child, panelId, updates),
    ),
  };
}

/**
 * Split a panel to add a new panel at a specific position.
 */
function splitPanelInTreeWithPosition(
  node: SplitNode,
  targetPanelId: string,
  newPanel: SplitPanel,
  orientation: SplitOrientation,
  addBefore: boolean,
): SplitNode {
  if (node.type === "panel") {
    if (node.panel.id === targetPanelId) {
      const children = addBefore
        ? [{ type: "panel" as const, panel: newPanel }, node]
        : [node, { type: "panel" as const, panel: newPanel }];
      return {
        type: "group",
        id: generateId("group"),
        orientation,
        children,
        sizes: [50, 50],
      };
    }
    return node;
  }

  const targetIndex = node.children.findIndex(
    (child) => child.type === "panel" && child.panel.id === targetPanelId,
  );

  if (targetIndex !== -1 && node.orientation === orientation) {
    const newChildren = [...node.children];
    const newSizes = [...node.sizes];
    const insertIndex = addBefore ? targetIndex : targetIndex + 1;
    newChildren.splice(insertIndex, 0, { type: "panel", panel: newPanel });
    const targetSize = newSizes[targetIndex];
    newSizes[targetIndex] = targetSize / 2;
    newSizes.splice(insertIndex, 0, targetSize / 2);

    return {
      ...node,
      children: newChildren,
      sizes: newSizes,
    };
  }

  return {
    ...node,
    children: node.children.map((child) =>
      splitPanelInTreeWithPosition(
        child,
        targetPanelId,
        newPanel,
        orientation,
        addBefore,
      ),
    ),
  };
}

// ============================================================================
// Helpers
// ============================================================================

function generateTabId(): string {
  return `tab-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function generateTabName(tabCount: number): string {
  return `Tab ${tabCount + 1}`;
}

/**
 * Get the panel type from a pathname.
 */
function getPanelTypeFromPathname(pathname: string): SplitPanelType {
  if (pathname.startsWith("/dashboards")) return "dashboard";
  if (pathname.startsWith("/trackers")) return "trackers";
  if (pathname.startsWith("/alerts")) return "alerts";
  if (pathname.startsWith("/enrichments")) return "enrichments";
  if (pathname.startsWith("/chat")) return "chat";
  if (pathname.startsWith("/search")) return "search";
  if (
    pathname.startsWith("/account") ||
    pathname.startsWith("/settings") ||
    pathname.startsWith("/plan")
  )
    return "account";
  if (pathname.startsWith("/widgets")) return "widgets";
  if (pathname.startsWith("/branches")) return "branches";
  if (pathname.startsWith("/investigations")) return "investigation";
  if (pathname.startsWith("/reports")) return "reports";
  if (pathname.startsWith("/workspaces")) return "workspaces";
  if (pathname.startsWith("/tokens")) return "tokens";
  if (pathname.startsWith("/members")) return "members";
  if (pathname.startsWith("/channels")) return "channels";
  if (pathname.startsWith("/organization")) return "organization";
  if (pathname.startsWith("/demo")) return "home"; // Demo pages map to home type
  return "home";
}

/**
 * Get a display title from a panel type.
 */
function getTitleFromPanelType(
  type: SplitPanelType,
  resourceId?: string,
): string {
  switch (type) {
    case "dashboard":
      return resourceId ? "Dashboard" : "Dashboards";
    case "trackers":
      return "Trackers";
    case "alerts":
      return "Alerts";
    case "enrichments":
      return "Enrichments";
    case "chat":
      return resourceId ? "Conversation" : "Chat";
    case "search":
      return "Search";
    case "widgets":
      return "Widgets";
    case "branches":
      return "Branches";
    case "investigation":
      return resourceId ? "Investigation" : "Investigations";
    case "reports":
      return "Reports";
    case "workspaces":
      return "Workspaces";
    case "tokens":
      return "Tokens";
    case "members":
      return "Members";
    case "channels":
      return "Channels";
    case "organization":
      return "Organization";
    case "account":
      return "Settings";
    default:
      return "Home";
  }
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: TabsState = {
  tabs: [],
  currentTabId: null,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useTabsStore = create<TabsStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // --- Tab Management ---

      getCurrentTab: () => {
        const state = get();
        if (!state.currentTabId) return null;
        return state.tabs.find((t) => t.id === state.currentTabId) || null;
      },

      createTab: (name?: string, initialRoot?: SplitNode | null) => {
        const state = get();
        const tabId = generateTabId();
        const tabName = name || generateTabName(state.tabs.length);

        const newTab: Tab = {
          id: tabId,
          name: tabName,
          root: initialRoot || null,
          focusedPanelId: initialRoot
            ? getAllPanels(initialRoot)[0]?.id || null
            : null,
          createdAt: Date.now(),
        };

        set({
          tabs: [...state.tabs, newTab],
          currentTabId: tabId,
        });

        logger.debug("[Tabs] Created new tab", { tabId, tabName });
        return tabId;
      },

      deleteTab: (tabId: string) => {
        const state = get();
        const remainingTabs = state.tabs.filter((t) => t.id !== tabId);

        let newCurrentTabId = state.currentTabId;
        if (state.currentTabId === tabId) {
          const currentIndex = state.tabs.findIndex((t) => t.id === tabId);
          if (remainingTabs.length > 0) {
            const newIndex = Math.min(currentIndex, remainingTabs.length - 1);
            newCurrentTabId = remainingTabs[newIndex]?.id || null;
          } else {
            newCurrentTabId = null;
          }
        }

        set({
          tabs: remainingTabs,
          currentTabId: newCurrentTabId,
        });

        logger.debug("[Tabs] Deleted tab", {
          tabId,
          remainingTabs: remainingTabs.length,
        });
      },

      selectTab: (tabId: string) => {
        const state = get();
        const tab = state.tabs.find((t) => t.id === tabId);

        if (!tab) {
          logger.warn("[Tabs] Tab not found", { tabId });
          return;
        }

        set({ currentTabId: tabId });
        logger.debug("[Tabs] Selected tab", { tabId, tabName: tab.name });
      },

      renameTab: (tabId: string, name: string) => {
        set((state) => ({
          tabs: state.tabs.map((t) => (t.id === tabId ? { ...t, name } : t)),
        }));
        logger.debug("[Tabs] Renamed tab", { tabId, name });
      },

      getTabs: () => get().tabs,

      ensureDefaultTab: () => {
        const state = get();
        if (state.tabs.length === 0) {
          const tabId = generateTabId();
          const newTab: Tab = {
            id: tabId,
            name: "Tab 1",
            root: null,
            focusedPanelId: null,
            createdAt: Date.now(),
          };

          set({
            tabs: [newTab],
            currentTabId: tabId,
          });

          logger.debug("[Tabs] Created default tab", { tabId });
          return tabId;
        }
        return state.currentTabId || state.tabs[0]?.id || "";
      },

      reset: () => {
        set(initialState);
        logger.debug("[Tabs] Reset to initial state");
      },

      goToPreviousTab: () => {
        const state = get();
        if (state.tabs.length <= 1) return;

        const currentIndex = state.tabs.findIndex(
          (t) => t.id === state.currentTabId,
        );
        if (currentIndex > 0) {
          const prevTab = state.tabs[currentIndex - 1];
          set({ currentTabId: prevTab.id });
          logger.debug("[Tabs] Navigated to previous tab", {
            tabId: prevTab.id,
          });
        }
      },

      goToNextTab: () => {
        const state = get();
        if (state.tabs.length <= 1) return;

        const currentIndex = state.tabs.findIndex(
          (t) => t.id === state.currentTabId,
        );
        if (currentIndex < state.tabs.length - 1) {
          const nextTab = state.tabs[currentIndex + 1];
          set({ currentTabId: nextTab.id });
          logger.debug("[Tabs] Navigated to next tab", { tabId: nextTab.id });
        }
      },

      goToTabByIndex: (index: number) => {
        const state = get();
        if (index >= 0 && index < state.tabs.length) {
          const tab = state.tabs[index];
          set({ currentTabId: tab.id });
          logger.debug("[Tabs] Navigated to tab by index", {
            index,
            tabId: tab.id,
          });
        }
      },

      getTabRoute: (tabId: string) => {
        const state = get();
        const tab = state.tabs.find((t) => t.id === tabId);
        if (!tab?.root) return null;

        const panels = getAllPanels(tab.root);
        const focusedPanel = tab.focusedPanelId
          ? panels.find((p) => p.id === tab.focusedPanelId)
          : panels[0];

        return focusedPanel?.route || null;
      },

      updateTabColor: (tabId: string, color: string) => {
        set((state) => ({
          tabs: state.tabs.map((t) => (t.id === tabId ? { ...t, color } : t)),
        }));
        logger.debug("[Tabs] Updated tab color", { tabId, color });
      },

      updateTabIcon: (tabId: string, icon: string) => {
        set((state) => ({
          tabs: state.tabs.map((t) => (t.id === tabId ? { ...t, icon } : t)),
        }));
        logger.debug("[Tabs] Updated tab icon", { tabId, icon });
      },

      cleanupEmptyTabs: () => {
        const state = get();

        // Don't cleanup if only one tab
        if (state.tabs.length <= 1) return;

        // Find empty tabs (tabs with no panels)
        const emptyTabIds = state.tabs
          .filter((tab) => {
            const panelCount = countPanels(tab.root);
            return panelCount === 0;
          })
          .map((tab) => tab.id);

        if (emptyTabIds.length === 0) return;

        // Don't delete all tabs - keep at least one
        const remainingTabs = state.tabs.filter(
          (t) => !emptyTabIds.includes(t.id),
        );
        if (remainingTabs.length === 0) return;

        // Update current tab if it's being deleted
        let newCurrentTabId = state.currentTabId;
        if (state.currentTabId && emptyTabIds.includes(state.currentTabId)) {
          const currentIndex = state.tabs.findIndex(
            (t) => t.id === state.currentTabId,
          );
          const newIndex = Math.min(currentIndex, remainingTabs.length - 1);
          newCurrentTabId =
            remainingTabs[newIndex]?.id || remainingTabs[0]?.id || null;
        }

        set({
          tabs: remainingTabs,
          currentTabId: newCurrentTabId,
        });

        logger.debug("[Tabs] Cleaned up empty tabs", {
          deletedTabIds: emptyTabIds,
          remainingTabs: remainingTabs.length,
        });
      },

      // --- Split View Actions (operate on current tab) ---

      isActive: () => {
        const currentTab = get().getCurrentTab();
        return countPanels(currentTab?.root || null) > 1;
      },

      getPanels: () => {
        const currentTab = get().getCurrentTab();
        return getAllPanels(currentTab?.root || null);
      },

      addPanel: (
        panelData,
        targetPanelId,
        orientation = "horizontal",
        addBefore = false,
      ) => {
        const state = get();
        const currentTab = state.getCurrentTab();

        if (!currentTab) {
          // Create a default tab first
          const tabId = state.ensureDefaultTab();
          // Re-get state after creating tab
          const newState = get();
          const tab = newState.tabs.find((t) => t.id === tabId);
          if (!tab) return "";

          const newPanel: SplitPanel = {
            ...panelData,
            id: generateId("panel"),
          };

          set({
            tabs: newState.tabs.map((t) =>
              t.id === tabId
                ? {
                    ...t,
                    root: { type: "panel", panel: newPanel },
                    focusedPanelId: newPanel.id,
                  }
                : t,
            ),
          });

          logger.debug("[Tabs] First panel added", { panelId: newPanel.id });
          return newPanel.id;
        }

        const newPanel: SplitPanel = {
          ...panelData,
          id: generateId("panel"),
        };

        let newRoot: SplitNode | null;

        if (!currentTab.root) {
          newRoot = { type: "panel", panel: newPanel };
        } else if (targetPanelId) {
          newRoot = splitPanelInTreeWithPosition(
            currentTab.root,
            targetPanelId,
            newPanel,
            orientation,
            addBefore,
          );
        } else {
          // No target: add to root level with smart grid layout
          if (currentTab.root.type === "panel") {
            newRoot = {
              type: "group",
              id: generateId("group"),
              orientation: "horizontal",
              children: [currentTab.root, { type: "panel", panel: newPanel }],
              sizes: [50, 50],
            };
          } else {
            const rootGroup = currentTab.root;
            const currentCount = countPanels(currentTab.root);

            if (currentCount === 2 && rootGroup.orientation === "horizontal") {
              newRoot = {
                type: "group",
                id: generateId("group"),
                orientation: "vertical",
                children: [rootGroup, { type: "panel", panel: newPanel }],
                sizes: [66, 34],
              };
            } else if (
              currentCount === 3 &&
              rootGroup.orientation === "vertical" &&
              rootGroup.children.length === 2
            ) {
              const topGroup = rootGroup.children[0];
              const bottomPanel = rootGroup.children[1];

              newRoot = {
                type: "group",
                id: rootGroup.id,
                orientation: "vertical",
                children: [
                  topGroup,
                  {
                    type: "group",
                    id: generateId("group"),
                    orientation: "horizontal",
                    children: [bottomPanel, { type: "panel", panel: newPanel }],
                    sizes: [50, 50],
                  },
                ],
                sizes: [50, 50],
              };
            } else {
              const newChildren = [
                ...rootGroup.children,
                { type: "panel" as const, panel: newPanel },
              ];
              const newSizes = rootGroup.sizes.map(
                (s) => (s * rootGroup.children.length) / newChildren.length,
              );
              newSizes.push(100 / newChildren.length);

              newRoot = {
                ...rootGroup,
                children: newChildren,
                sizes: newSizes,
              };
            }
          }
        }

        set({
          tabs: state.tabs.map((t) =>
            t.id === currentTab.id
              ? { ...t, root: newRoot, focusedPanelId: newPanel.id }
              : t,
          ),
        });

        logger.debug("[Tabs] Panel added", {
          panelId: newPanel.id,
          targetPanelId,
          totalPanels: countPanels(newRoot),
        });

        return newPanel.id;
      },

      removePanel: (panelId: string) => {
        const state = get();
        const currentTab = state.getCurrentTab();

        if (!currentTab?.root) {
          logger.warn("[Tabs] No panels to remove");
          return;
        }

        const newRoot = removePanelFromTree(currentTab.root, panelId);
        const panels = getAllPanels(newRoot);

        let newFocusedPanelId = currentTab.focusedPanelId;
        if (currentTab.focusedPanelId === panelId) {
          newFocusedPanelId = panels.length > 0 ? panels[0].id : null;
        }

        // If no panels left and there are other tabs, delete this tab
        if (panels.length === 0 && state.tabs.length > 1) {
          const remainingTabs = state.tabs.filter(
            (t) => t.id !== currentTab.id,
          );
          const currentIndex = state.tabs.findIndex(
            (t) => t.id === currentTab.id,
          );
          const newIndex = Math.min(currentIndex, remainingTabs.length - 1);
          const newCurrentTabId = remainingTabs[newIndex]?.id || null;

          set({
            tabs: remainingTabs,
            currentTabId: newCurrentTabId,
          });

          logger.debug("[Tabs] Empty tab auto-deleted", {
            deletedTabId: currentTab.id,
            remainingTabs: remainingTabs.length,
          });
          return;
        }

        set({
          tabs: state.tabs.map((t) =>
            t.id === currentTab.id
              ? { ...t, root: newRoot, focusedPanelId: newFocusedPanelId }
              : t,
          ),
        });

        logger.debug("[Tabs] Panel removed", {
          panelId,
          remainingPanels: panels.length,
        });
      },

      setFocusedPanelId: (panelId: string | null) => {
        const state = get();
        const currentTab = state.getCurrentTab();

        if (!currentTab) return;

        set({
          tabs: state.tabs.map((t) =>
            t.id === currentTab.id ? { ...t, focusedPanelId: panelId } : t,
          ),
        });
      },

      updateGroupSizes: (groupId: string, sizes: number[]) => {
        const state = get();
        const currentTab = state.getCurrentTab();

        if (!currentTab?.root || currentTab.root.type === "panel") return;

        const newRoot = updateGroupInTree(
          currentTab.root,
          groupId,
          (group) => ({
            ...group,
            sizes,
          }),
        );

        set({
          tabs: state.tabs.map((t) =>
            t.id === currentTab.id ? { ...t, root: newRoot } : t,
          ),
        });
      },

      updatePanel: (
        panelId: string,
        updates: Partial<Omit<SplitPanel, "id">>,
      ) => {
        const state = get();
        const currentTab = state.getCurrentTab();

        if (!currentTab?.root) return;

        const newRoot = updatePanelInTree(currentTab.root, panelId, updates);

        set({
          tabs: state.tabs.map((t) =>
            t.id === currentTab.id ? { ...t, root: newRoot } : t,
          ),
        });
      },

      swapPanels: (panelIdA: string, panelIdB: string) => {
        const state = get();
        const currentTab = state.getCurrentTab();

        if (!currentTab?.root) return;

        const panelA = findPanelInTree(currentTab.root, panelIdA);
        const panelB = findPanelInTree(currentTab.root, panelIdB);

        if (!panelA || !panelB) {
          logger.warn("[Tabs] Cannot swap: panel not found", {
            panelIdA,
            panelIdB,
          });
          return;
        }

        let newRoot = updatePanelInTree(currentTab.root, panelIdA, {
          type: panelB.type,
          route: panelB.route,
          resourceId: panelB.resourceId,
          title: panelB.title,
          icon: panelB.icon,
          color: panelB.color,
        });
        newRoot = updatePanelInTree(newRoot, panelIdB, {
          type: panelA.type,
          route: panelA.route,
          resourceId: panelA.resourceId,
          title: panelA.title,
          icon: panelA.icon,
          color: panelA.color,
        });

        set({
          tabs: state.tabs.map((t) =>
            t.id === currentTab.id ? { ...t, root: newRoot } : t,
          ),
        });

        logger.debug("[Tabs] Panels swapped", { panelIdA, panelIdB });
      },

      movePanel: (sourcePanelId, targetPanelId, position) => {
        const state = get();
        const currentTab = state.getCurrentTab();

        if (!currentTab?.root) return;
        if (sourcePanelId === targetPanelId) return;

        const sourcePanel = findPanelInTree(currentTab.root, sourcePanelId);
        if (!sourcePanel) {
          logger.warn("[Tabs] Cannot move: source panel not found", {
            sourcePanelId,
          });
          return;
        }

        let newRoot = removePanelFromTree(currentTab.root, sourcePanelId);
        if (!newRoot) {
          logger.warn("[Tabs] Cannot move: would leave no panels");
          return;
        }

        const orientation: SplitOrientation =
          position === "left" || position === "right"
            ? "horizontal"
            : "vertical";

        const movedPanel: SplitPanel = {
          ...sourcePanel,
          id: generateId("panel"),
        };

        const addBefore = position === "left" || position === "top";

        newRoot = splitPanelInTreeWithPosition(
          newRoot,
          targetPanelId,
          movedPanel,
          orientation,
          addBefore,
        );

        set({
          tabs: state.tabs.map((t) =>
            t.id === currentTab.id
              ? { ...t, root: newRoot, focusedPanelId: movedPanel.id }
              : t,
          ),
        });

        logger.debug("[Tabs] Panel moved", {
          sourcePanelId,
          targetPanelId,
          position,
          newPanelId: movedPanel.id,
        });
      },

      initializeWithPanel: (panelData) => {
        const state = get();
        let currentTab = state.getCurrentTab();

        if (!currentTab) {
          state.ensureDefaultTab();
          currentTab = get().getCurrentTab();
        }

        if (!currentTab) return "";

        const newPanel: SplitPanel = {
          ...panelData,
          id: generateId("panel"),
        };

        set({
          tabs: get().tabs.map((t) =>
            t.id === currentTab!.id
              ? {
                  ...t,
                  root: { type: "panel", panel: newPanel },
                  focusedPanelId: newPanel.id,
                }
              : t,
          ),
        });

        logger.debug("[Tabs] Initialized with panel", { panelId: newPanel.id });
        return newPanel.id;
      },

      setRoot: (root: SplitNode | null) => {
        const state = get();
        const currentTab = state.getCurrentTab();

        if (!currentTab) return;

        const panels = getAllPanels(root);
        const focusedPanelId = panels.length > 0 ? panels[0].id : null;

        set({
          tabs: state.tabs.map((t) =>
            t.id === currentTab.id ? { ...t, root, focusedPanelId } : t,
          ),
        });

        logger.debug("[Tabs] Root set directly", {
          panelCount: panels.length,
          focusedPanelId,
        });
      },

      movePanelToTab: (panel: SplitPanel, targetTabId: string) => {
        const state = get();
        const targetTab = state.tabs.find((t) => t.id === targetTabId);

        if (!targetTab) {
          logger.warn("[Tabs] Target tab not found", { targetTabId });
          return;
        }

        let newRoot: SplitNode;
        if (!targetTab.root) {
          newRoot = { type: "panel", panel };
        } else if (targetTab.root.type === "panel") {
          newRoot = {
            type: "group",
            id: generateId("group"),
            orientation: "horizontal",
            children: [targetTab.root, { type: "panel", panel }],
            sizes: [50, 50],
          };
        } else {
          const group = targetTab.root;
          newRoot = {
            ...group,
            children: [...group.children, { type: "panel", panel }],
            sizes: [
              ...group.sizes.map(
                (s) =>
                  (s * group.children.length) / (group.children.length + 1),
              ),
              100 / (group.children.length + 1),
            ],
          };
        }

        set({
          tabs: state.tabs.map((t) =>
            t.id === targetTabId
              ? { ...t, root: newRoot, focusedPanelId: panel.id }
              : t,
          ),
        });

        logger.debug("[Tabs] Moved panel to tab", {
          panelId: panel.id,
          targetTabId,
          targetTabName: targetTab.name,
        });
      },

      syncWithRoute: (pathname: string, search: string) => {
        // Wait for persisted state to load before syncing —
        // otherwise we'd create a new tab from scratch only to have
        // the hydrated state overwrite it, causing a tab ID change
        // and the duplicate-view animation glitch.
        if (!useTabsStore.persist.hasHydrated()) {
          logger.debug(
            "[Tabs] syncWithRoute: Skipped — store not yet hydrated",
          );
          return;
        }

        const state = get();
        const route = pathname + search;
        const panelType = getPanelTypeFromPathname(pathname);
        const searchParams = new URLSearchParams(search);
        const resourceId = searchParams.get("id") || undefined;
        const title = getTitleFromPanelType(panelType, resourceId);

        // Get or create current tab
        let currentTab = state.getCurrentTab();

        // CASE 1: No tabs exist - create one with the panel
        // This happens on initial page load or when all tabs have been closed
        if (!currentTab) {
          const tabId = generateTabId();
          const newPanel: SplitPanel = {
            id: generateId("panel"),
            type: panelType,
            route,
            resourceId,
            title,
          };

          const newTab: Tab = {
            id: tabId,
            name: "Tab 1",
            root: { type: "panel", panel: newPanel },
            focusedPanelId: newPanel.id,
            createdAt: Date.now(),
          };

          set({
            tabs: [newTab],
            currentTabId: tabId,
          });

          logger.debug("[Tabs] syncWithRoute: Created new tab with panel", {
            tabId,
            route,
            panelType,
          });
          return;
        }

        // CASE 2: Tab exists but has no panels - initialize with the route
        // This happens if a tab was created empty
        if (!currentTab.root) {
          const newPanel: SplitPanel = {
            id: generateId("panel"),
            type: panelType,
            route,
            resourceId,
            title,
          };

          set({
            tabs: state.tabs.map((t) =>
              t.id === currentTab!.id
                ? {
                    ...t,
                    root: { type: "panel", panel: newPanel },
                    focusedPanelId: newPanel.id,
                  }
                : t,
            ),
          });

          logger.debug("[Tabs] syncWithRoute: Initialized tab with panel", {
            tabId: currentTab.id,
            route,
            panelType,
          });
          return;
        }

        // Tab has panels - check if sync is actually needed
        const panels = getAllPanels(currentTab.root);
        const focusedPanel = currentTab.focusedPanelId
          ? panels.find((p) => p.id === currentTab!.focusedPanelId)
          : panels[0];

        // EARLY EXIT: Check if ANY panel already has this exact route
        // This prevents unnecessary updates when the route is already represented
        const panelWithRoute = panels.find((p) => p.route === route);
        if (panelWithRoute) {
          // Route already exists in a panel - just ensure it's focused if it's not
          if (currentTab.focusedPanelId !== panelWithRoute.id) {
            set({
              tabs: state.tabs.map((t) =>
                t.id === currentTab!.id
                  ? { ...t, focusedPanelId: panelWithRoute.id }
                  : t,
              ),
            });
            logger.debug(
              "[Tabs] syncWithRoute: Focused existing panel with matching route",
              {
                panelId: panelWithRoute.id,
                route,
              },
            );
          }
          return; // No update needed - route already exists
        }

        // CASE 3: Single panel - update it to match the route
        // This is the common case for non-split-view navigation
        if (panels.length === 1 && focusedPanel) {
          const newRoot = updatePanelInTree(currentTab.root, focusedPanel.id, {
            type: panelType,
            route,
            resourceId,
            title,
          });

          set({
            tabs: state.tabs.map((t) =>
              t.id === currentTab!.id ? { ...t, root: newRoot } : t,
            ),
          });

          logger.debug("[Tabs] syncWithRoute: Updated single panel route", {
            panelId: focusedPanel.id,
            oldRoute: focusedPanel.route,
            newRoute: route,
          });
          return;
        }

        // CASE 4: Multiple panels - only update focused panel if types match
        // This prevents cross-panel interference in split view
        if (focusedPanel && focusedPanel.type === panelType) {
          const newRoot = updatePanelInTree(currentTab.root, focusedPanel.id, {
            route,
            resourceId,
            title,
          });

          set({
            tabs: state.tabs.map((t) =>
              t.id === currentTab!.id ? { ...t, root: newRoot } : t,
            ),
          });

          logger.debug(
            "[Tabs] syncWithRoute: Updated focused panel route (same type)",
            {
              panelId: focusedPanel.id,
              route,
            },
          );
          return;
        }

        // CASE 5: Multiple panels, types don't match
        // Don't sync - the user might be using split view panel focus
        // which already handles its own route updates via navigateTo
        logger.debug(
          "[Tabs] syncWithRoute: Skipped - panel type mismatch in split view",
          {
            focusedPanelType: focusedPanel?.type,
            routeType: panelType,
            route,
          },
        );
      },
    }),
    {
      name: "tabs-store-v2",
      version: 2,
      partialize: (state) => ({
        tabs: state.tabs,
        currentTabId: state.currentTabId,
      }),
    },
  ),
);

// ============================================================================
// Hydration Hook
// ============================================================================

/**
 * Hook that returns true once the persisted store has finished hydrating.
 * Components that depend on persisted state should gate on this before
 * performing side-effects that read or write tabs.
 */
export function useTabsStoreHydrated(): boolean {
  const [hydrated, setHydrated] = React.useState(
    useTabsStore.persist.hasHydrated(),
  );

  React.useEffect(() => {
    const unsub = useTabsStore.persist.onFinishHydration(() => {
      setHydrated(true);
    });
    // In case hydration completed before the listener was attached
    if (useTabsStore.persist.hasHydrated()) {
      setHydrated(true);
    }
    return unsub;
  }, []);

  return hydrated;
}

// ============================================================================
// Selector Hooks
// ============================================================================

/**
 * Hook to get all tabs.
 */
export function useTabs(): Tab[] {
  return useTabsStore(useShallow((state) => state.tabs));
}

/**
 * Hook to get current tab ID.
 */
export function useCurrentTabId(): string | null {
  return useTabsStore((state) => state.currentTabId);
}

/**
 * Hook to get current tab.
 */
export function useCurrentTab(): Tab | null {
  return useTabsStore((state) => {
    if (!state.currentTabId) return null;
    return state.tabs.find((t) => t.id === state.currentTabId) || null;
  });
}

/**
 * Hook to check if there are multiple tabs.
 */
export function useHasMultipleTabs(): boolean {
  return useTabsStore((state) => state.tabs.length > 1);
}

/**
 * Hook to get tab count.
 */
export function useTabCount(): number {
  return useTabsStore((state) => state.tabs.length);
}

/**
 * Hook to get current tab index.
 */
export function useCurrentTabIndex(): number {
  return useTabsStore((state) => {
    if (!state.currentTabId) return -1;
    return state.tabs.findIndex((t) => t.id === state.currentTabId);
  });
}

/**
 * Hook to get tabs actions (tab management).
 * Uses a single selector to get all actions at once for better performance.
 * Actions are stable references from Zustand, so the returned object is memoized.
 */
export function useTabsActions() {
  return useTabsStore(
    useShallow((state) => ({
      createTab: state.createTab,
      deleteTab: state.deleteTab,
      selectTab: state.selectTab,
      renameTab: state.renameTab,
      movePanelToTab: state.movePanelToTab,
      ensureDefaultTab: state.ensureDefaultTab,
      reset: state.reset,
      goToPreviousTab: state.goToPreviousTab,
      goToNextTab: state.goToNextTab,
      goToTabByIndex: state.goToTabByIndex,
      getTabRoute: state.getTabRoute,
      updateTabColor: state.updateTabColor,
      updateTabIcon: state.updateTabIcon,
      cleanupEmptyTabs: state.cleanupEmptyTabs,
      syncWithRoute: state.syncWithRoute,
    })),
  );
}

// ============================================================================
// Split View Selector Hooks (for current tab)
// ============================================================================

/**
 * Combined split view state - returns all split view state in a single atomic read.
 * This prevents inconsistent states during tab transitions.
 */
interface SplitViewState {
  root: SplitNode | null;
  focusedPanelId: string | null;
  isActive: boolean;
  panelCount: number;
  panels: SplitPanel[];
}

/**
 * Hook to get all split view state atomically.
 * Use this in components that need multiple pieces of split view state
 * to avoid inconsistent intermediate states during tab switches.
 *
 * Implementation: Uses separate selectors for object refs vs primitives
 * to ensure proper change detection while avoiding infinite loops.
 */
export function useSplitViewState(): SplitViewState {
  // Get the root directly - this selector returns the same object reference
  // as long as the tab's root hasn't changed
  const root = useTabsStore((state) => {
    const currentTab = state.currentTabId
      ? state.tabs.find((t) => t.id === state.currentTabId)
      : null;
    return currentTab?.root || null;
  });

  // Get primitive values with useShallow for stable object comparison
  const primitives = useTabsStore(
    useShallow((state) => {
      const currentTab = state.currentTabId
        ? state.tabs.find((t) => t.id === state.currentTabId)
        : null;
      const tabRoot = currentTab?.root || null;
      const panelCount = countPanels(tabRoot);
      return {
        focusedPanelId: currentTab?.focusedPanelId || null,
        isActive: panelCount > 1,
        panelCount,
      };
    }),
  );

  // Derive panels from root (cached via getAllPanels)
  const panels = getAllPanels(root);

  return {
    root,
    ...primitives,
    panels,
  };
}

/**
 * Hook to check if split view is active (current tab has > 1 panel).
 */
export function useSplitViewActive(): boolean {
  return useTabsStore((state) => {
    const currentTab = state.currentTabId
      ? state.tabs.find((t) => t.id === state.currentTabId)
      : null;
    return countPanels(currentTab?.root || null) > 1;
  });
}

/**
 * Hook to get panels from current tab.
 */
export function useSplitViewPanels(): SplitPanel[] {
  return useTabsStore(
    useShallow((state) => {
      const currentTab = state.currentTabId
        ? state.tabs.find((t) => t.id === state.currentTabId)
        : null;
      return getAllPanels(currentTab?.root || null);
    }),
  );
}

/**
 * Hook to get root node of current tab.
 */
export function useSplitViewRoot(): SplitNode | null {
  return useTabsStore((state) => {
    const currentTab = state.currentTabId
      ? state.tabs.find((t) => t.id === state.currentTabId)
      : null;
    return currentTab?.root || null;
  });
}

/**
 * Hook to get focused panel ID of current tab.
 */
export function useSplitViewFocusedPanelId(): string | null {
  return useTabsStore((state) => {
    const currentTab = state.currentTabId
      ? state.tabs.find((t) => t.id === state.currentTabId)
      : null;
    return currentTab?.focusedPanelId || null;
  });
}

/**
 * Hook to get the focused panel's type from the current tab.
 * Optimized to only trigger re-renders when the type actually changes.
 * Useful for sidebar and other components that only need the panel type.
 */
export function useFocusedPanelType(): SplitPanelType | null {
  return useTabsStore((state) => {
    const currentTab = state.currentTabId
      ? state.tabs.find((t) => t.id === state.currentTabId)
      : null;
    if (!currentTab?.root) return null;

    const focusedPanel = currentTab.focusedPanelId
      ? findPanelInTree(currentTab.root, currentTab.focusedPanelId)
      : null;

    // Fallback to first panel if no focused panel
    if (!focusedPanel && currentTab.root.type === "panel") {
      return currentTab.root.panel.type;
    }

    return focusedPanel?.type || null;
  });
}

/**
 * Hook to get the focused panel from the current tab.
 * Uses useShallow for proper memoization of the panel object.
 * Only re-renders when the focused panel's properties actually change.
 */
export function useFocusedPanel(): SplitPanel | null {
  return useTabsStore(
    useShallow((state) => {
      const currentTab = state.currentTabId
        ? state.tabs.find((t) => t.id === state.currentTabId)
        : null;
      if (!currentTab?.root) return null;

      const focusedPanel = currentTab.focusedPanelId
        ? findPanelInTree(currentTab.root, currentTab.focusedPanelId)
        : null;

      // Fallback to first panel if no focused panel
      if (!focusedPanel && currentTab.root.type === "panel") {
        return currentTab.root.panel;
      }

      return focusedPanel;
    }),
  );
}

/**
 * Hook to get split view actions.
 * Uses a single selector to get all actions at once for better performance.
 * Actions are stable references from Zustand, so the returned object is memoized.
 */
export function useSplitViewActions() {
  return useTabsStore(
    useShallow((state) => ({
      addPanel: state.addPanel,
      removePanel: state.removePanel,
      setFocusedPanelId: state.setFocusedPanelId,
      updateGroupSizes: state.updateGroupSizes,
      updatePanel: state.updatePanel,
      swapPanels: state.swapPanels,
      movePanel: state.movePanel,
      initializeWithPanel: state.initializeWithPanel,
      setRoot: state.setRoot,
      isActive: state.isActive,
      getPanels: state.getPanels,
      reset: state.reset,
    })),
  );
}

// Re-export types from split-view-store for convenience
export type {
  SplitNode,
  SplitPanel,
  SplitPanelType,
  SplitOrientation,
  SplitGroupNode,
  SplitPanelNode,
} from "@/hooks/use-split-view-store/types";
export {
  getAllPanels,
  countPanels,
  findPanelInTree,
} from "@/hooks/use-split-view-store/types";
